import React, { useState, useEffect } from "react";
import { withRouter } from "react-router-dom";
import {
  getProfileNotifications,
  markAsReadProfileNotification,
  markAllReadProfileNotification,
  dismissProfileNotification,
  dismissAllProfileNotifications,
} from "../../../api/index.js";
import style from "./NotificationDropdown.module.scss";

const NotificationDropdown = ({ history, onClose, onNotificationsUpdate, refreshTrigger = 0 }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  // refreshKey: bumped internally when an action needs to re-fetch the current page
  // (e.g. dismissing the last item leaves the page empty and pagination must collapse).
  // refreshTrigger (prop): bumped by the parent on external events (e.g. realtime push).
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let isMounted = true;

    const fetchNotifications = async () => {
      try {
        setLoading(true);
        const response = await getProfileNotifications({ page: currentPage });

        if (!isMounted) return;

        if ((response?.success || response?.status) && response?.data) {
          const paginatedData = response.data;
          setNotifications(paginatedData.data || []);
          setTotalPages(paginatedData.last_page || 1);

          if (
            (paginatedData.data || []).length === 0 &&
            currentPage > 1 &&
            (paginatedData.last_page || 1) < currentPage
          ) {
            setCurrentPage(paginatedData.last_page || 1);
          }
        }
      } catch (error) {
        if (isMounted) {
          console.error("Error fetching notifications:", error);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchNotifications();

    return () => {
      isMounted = false;
    };
  }, [currentPage, refreshKey, refreshTrigger]);

  const handleMarkAsRead = async (notificationId) => {
    try {
      const response = await markAsReadProfileNotification(notificationId);
      if (response?.success) {
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notificationId ? { ...n, read: true, read_at: new Date().toISOString() } : n
          )
        );
        if (onNotificationsUpdate) {
          onNotificationsUpdate();
        }
      } else if (response?.status) {
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notificationId ? { ...n, read: true, read_at: new Date().toISOString() } : n
          )
        );
        if (onNotificationsUpdate) {
          onNotificationsUpdate();
        }
      }
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const response = await markAllReadProfileNotification();
      if (response?.success || response?.status) {
        setNotifications((prev) =>
          prev.map((n) => ({ ...n, read: true, read_at: new Date().toISOString() }))
        );
        if (onNotificationsUpdate) {
          onNotificationsUpdate();
        }
      }
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  const handleDismiss = async (event, notificationId) => {
    event.stopPropagation();
    try {
      const response = await dismissProfileNotification(notificationId);
      if (response?.success || response?.status) {
        const wasLastOnPage = notifications.length === 1;
        setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
        if (wasLastOnPage) {
          setRefreshKey((k) => k + 1);
        }
        if (onNotificationsUpdate) {
          onNotificationsUpdate();
        }
      }
    } catch (error) {
      console.error("Error dismissing notification:", error);
    }
  };

  const handleClearAll = async () => {
    try {
      const response = await dismissAllProfileNotifications();
      if (response?.success || response?.status) {
        setNotifications([]);
        if (currentPage !== 1) {
          setCurrentPage(1);
        } else {
          setRefreshKey((k) => k + 1);
        }
        if (onNotificationsUpdate) {
          onNotificationsUpdate();
        }
      }
    } catch (error) {
      console.error("Error clearing notifications:", error);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleSettingsClick = () => {
    history.push("/v2/profile/notification-settings");
    onClose();
  };

  const formatTime = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays === 1) return "Yesterday";
      if (diffDays < 7) return `${diffDays}d ago`;
      
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? "PM" : "AM";
      const displayHours = hours % 12 || 12;
      const displayMinutes = minutes.toString().padStart(2, "0");
      return `${displayHours}:${displayMinutes} ${ampm}`;
    } catch (error) {
      return "";
    }
  };

  const getIconBgColor = (type) => {
    switch (type) {
      case "warning":
      case "danger":
        return "#FFE7E4";
      default:
        return "#EDEDF4";
    }
  };

  return (
    <div className={style.notification_dropdown}>
      <div className={style.notification_tip}></div>
      <div className={style.notification_header}>
        <div className={style.notification_header_content}>
          <span className={style.notification_title}>Notifications</span>
          <div className={style.notification_header_actions}>
            <button
              className={style.notification_mark_all}
              onClick={handleMarkAllAsRead}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9.3507 5.35681L3.7507 10.8568C3.65718 10.9488 3.53125 11.0004 3.40007 11.0004C3.2689 11.0004 3.14297 10.9488 3.04945 10.8568L0.64945 8.49993C0.602625 8.45389 0.565328 8.39907 0.539689 8.33861C0.514049 8.27815 0.500569 8.21324 0.500018 8.14757C0.499466 8.0819 0.511855 8.01677 0.536475 7.95589C0.561096 7.895 0.597467 7.83957 0.643512 7.79275C0.689557 7.74592 0.744374 7.70862 0.804832 7.68298C0.865291 7.65734 0.930208 7.64386 0.995876 7.64331C1.06154 7.64276 1.12668 7.65515 1.18756 7.67977C1.24844 7.70439 1.30388 7.74076 1.3507 7.78681L3.40007 9.79931L8.65007 4.64306C8.74472 4.55007 8.87244 4.49848 9.00512 4.49965C9.07082 4.50024 9.13576 4.51375 9.19623 4.53943C9.25671 4.5651 9.31153 4.60244 9.35757 4.64931C9.40362 4.69617 9.43998 4.75165 9.46459 4.81257C9.48919 4.87349 9.50156 4.93865 9.50098 5.00435C9.5004 5.07005 9.48688 5.13499 9.4612 5.19546C9.43553 5.25594 9.39819 5.31076 9.35132 5.35681H9.3507ZM15.3569 4.64931C15.3109 4.60241 15.2561 4.56504 15.1956 4.53934C15.1352 4.51364 15.0702 4.50011 15.0045 4.49953C14.9388 4.49895 14.8736 4.51133 14.8127 4.53596C14.7518 4.56058 14.6963 4.59698 14.6494 4.64306L9.39945 9.79931L8.22257 8.64306C8.12793 8.55015 8.00025 8.49864 7.86762 8.49988C7.735 8.50111 7.6083 8.55497 7.51539 8.64962C7.42248 8.74427 7.37097 8.87195 7.37221 9.00457C7.37344 9.1372 7.4273 9.2639 7.52195 9.35681L9.04882 10.8568C9.14234 10.9488 9.26827 11.0004 9.39945 11.0004C9.53063 11.0004 9.65655 10.9488 9.75007 10.8568L15.3501 5.35681C15.397 5.31082 15.4344 5.25604 15.4602 5.19558C15.4859 5.13513 15.4995 5.07019 15.5002 5.00448C15.5008 4.93877 15.4885 4.87358 15.4639 4.81264C15.4393 4.7517 15.403 4.69619 15.3569 4.64931Z" fill="#254FE2"/>
              </svg>
              <span>Mark all as read</span>
            </button>
            <button
              className={style.notification_clear_all}
              onClick={handleClearAll}
              disabled={notifications.length === 0}
            >
              <span>Clear all</span>
            </button>
            <button
              className={style.notification_settings}
              onClick={handleSettingsClick}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M2 4C1.72386 4 1.5 4.22386 1.5 4.5C1.5 4.77614 1.72386 5 2 5H14C14.2761 5 14.5 4.77614 14.5 4.5C14.5 4.22386 14.2761 4 14 4H2Z"
                  fill="#3A383F"
                />
                <path
                  d="M2 7.5C1.72386 7.5 1.5 7.72386 1.5 8C1.5 8.27614 1.72386 8.5 2 8.5H14C14.2761 8.5 14.5 8.27614 14.5 8C14.5 7.72386 14.2761 7.5 14 7.5H2Z"
                  fill="#3A383F"
                />
                <path
                  d="M2 11C1.72386 11 1.5 11.2239 1.5 11.5C1.5 11.7761 1.72386 12 2 12H14C14.2761 12 14.5 11.7761 14.5 11.5C14.5 11.2239 14.2761 11 14 11H2Z"
                  fill="#3A383F"
                />
                <circle cx="5" cy="4.5" r="1.5" fill="#3A383F"/>
                <circle cx="11" cy="8" r="1.5" fill="#3A383F"/>
                <circle cx="5" cy="11.5" r="1.5" fill="#3A383F"/>
              </svg>
            </button>
          </div>
        </div>
        <div className={style.notification_divider}></div>
      </div>

      <div className={style.notification_list}>
        {loading ? (
          <div className={style.notification_loading}>Loading...</div>
        ) : notifications.length === 0 ? (
          <div className={style.notification_empty}>No notifications</div>
        ) : (
          notifications.map((notification) => (
            <div
              key={notification.id}
              className={`${style.notification_item} ${
                !notification.read ? style.notification_item_unread : ""
              }`}
              onClick={() => !notification.read && handleMarkAsRead(notification.id)}
            >
              <div
                className={style.notification_icon_box}
                style={{ background: getIconBgColor(notification.type) }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    d="M8 0C7.46957 0 6.96086 0.210714 6.58579 0.585786C6.21071 0.960859 6 1.46957 6 2V3C4.34315 3 2 3.89543 2 6V10C2 10.5304 2.21071 11.0391 2.58579 11.4142C2.96086 11.7893 3.46957 12 4 12H5V13C5 13.5304 5.21071 14.0391 5.58579 14.4142C5.96086 14.7893 6.46957 15 7 15H9C9.53043 15 10.0391 14.7893 10.4142 14.4142C10.7893 14.0391 11 13.5304 11 13V12H12C12.5304 12 13.0391 11.7893 13.4142 11.4142C13.7893 11.0391 14 10.5304 14 10V6C14 3.89543 11.6569 3 10 3V2C10 1.46957 9.78929 0.960859 9.41421 0.585786C9.03914 0.210714 8.53043 0 8 0ZM8 1C8.26522 1 8.51957 1.10536 8.70711 1.29289C8.89464 1.48043 9 1.73478 9 2V3H7V2C7 1.73478 7.10536 1.48043 7.29289 1.29289C7.48043 1.10536 7.73478 1 8 1ZM3 6C3 4.89543 4.34315 4 6 4H10C11.6569 4 13 4.89543 13 6V10C13 10.2652 12.8946 10.5196 12.7071 10.7071C12.5196 10.8946 12.2652 11 12 11H4C3.73478 11 3.48043 10.8946 3.29289 10.7071C3.10536 10.5196 3 10.2652 3 10V6ZM6 13C6 12.7348 6.10536 12.4804 6.29289 12.2929C6.48043 12.1054 6.73478 12 7 12H9C9.26522 12 9.51957 12.1054 9.70711 12.2929C9.89464 12.4804 10 12.7348 10 13V12H6V13Z"
                    fill="#3A383F"
                  />
                </svg>
              </div>
              <div className={style.notification_content}>
                <div className={style.notification_item_header}>
                  <span className={style.notification_item_title}>
                    {notification.title}
                  </span>
                  <span className={style.notification_item_time}>
                    {formatTime(notification.created_at)}
                  </span>
                </div>
                <p className={style.notification_item_message}>
                  {notification.description || ""}
                </p>
              </div>
              {!notification.read && (
                <span className={style.notification_item_dot}></span>
              )}
              <button
                type="button"
                className={style.notification_item_dismiss}
                onClick={(e) => handleDismiss(e, notification.id)}
                aria-label="Dismiss notification"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                >
                  <path
                    d="M9.354 2.646a.5.5 0 0 1 0 .708L6.707 6l2.647 2.646a.5.5 0 1 1-.708.708L6 6.707 3.354 9.354a.5.5 0 1 1-.708-.708L5.293 6 2.646 3.354a.5.5 0 1 1 .708-.708L6 5.293l2.646-2.647a.5.5 0 0 1 .708 0Z"
                    fill="#626168"
                  />
                </svg>
              </button>
            </div>
          ))
        )}
      </div>

      {totalPages > 1 && (
        <div className={style.notification_pagination}>
          <button
            className={`${style.pagination_button} ${currentPage === 1 ? style.disabled : ""}`}
            onClick={handlePrevious}
            disabled={currentPage === 1}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                d="M10.3538 3.35384C10.4002 3.30738 10.4371 3.25223 10.4622 3.19154C10.4874 3.13084 10.5003 3.06579 10.5003 3.00009C10.5003 2.93439 10.4874 2.86934 10.4622 2.80864C10.4371 2.74794 10.4002 2.69279 10.3538 2.64634C10.3073 2.59988 10.2522 2.56303 10.1915 2.53789C10.1308 2.51275 10.0657 2.49981 10 2.49981C9.93433 2.49981 9.86928 2.51275 9.80858 2.53789C9.74788 2.56303 9.69273 2.59988 9.64628 2.64634L4.64628 7.64634C4.59979 7.69277 4.56291 7.74792 4.53775 7.80862C4.51259 7.86932 4.49963 7.93438 4.49963 8.00009C4.49963 8.0658 4.51259 8.13086 4.53775 8.19156C4.56291 8.25226 4.59979 8.3074 4.64628 8.35384L9.64628 13.3538C9.7401 13.4477 9.86735 13.5004 10 13.5004C10.1327 13.5004 10.26 13.4477 10.3538 13.3538C10.4476 13.26 10.5003 13.1328 10.5003 13.0001C10.5003 12.8674 10.4476 12.7402 10.3538 12.6463L5.7069 8.00009L10.3538 3.35384Z"
                fill="#3A383F"
              />
            </svg>
          </button>
          <span className={style.pagination_info}>
            {currentPage} / {totalPages}
          </span>
          <button
            className={`${style.pagination_button} ${currentPage === totalPages ? style.disabled : ""}`}
            onClick={handleNext}
            disabled={currentPage === totalPages}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                d="M11.3537 8.35391L6.35372 13.3539C6.30727 13.4004 6.25212 13.4372 6.19142 13.4624C6.13072 13.4875 6.06567 13.5004 5.99997 13.5004C5.93428 13.5004 5.86922 13.4875 5.80852 13.4624C5.74783 13.4372 5.69268 13.4004 5.64622 13.3539C5.59977 13.3075 5.56292 13.2523 5.53778 13.1916C5.51263 13.1309 5.49969 13.0659 5.49969 13.0002C5.49969 12.9345 5.51263 12.8694 5.53778 12.8087C5.56292 12.748 5.59977 12.6929 5.64622 12.6464L10.2931 8.00016L5.64622 3.35391C5.5524 3.26009 5.49969 3.13284 5.49969 3.00016C5.49969 2.86747 5.5524 2.74023 5.64622 2.64641C5.74004 2.55259 5.86729 2.49988 5.99997 2.49988C6.13265 2.49988 6.2599 2.55259 6.35372 2.64641L11.3537 7.64641C11.4002 7.69284 11.4371 7.74799 11.4623 7.80869C11.4874 7.86938 11.5004 7.93445 11.5004 8.00016C11.5004 8.06586 11.4874 8.13093 11.4623 8.19163C11.4371 8.25233 11.4002 8.30747 11.3537 8.35391Z"
                fill="#3A383F"
              />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default withRouter(NotificationDropdown);

